// React Components
import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";

// Material UI Components
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

// Audit Vault Components
import ServiceIsDown from "../components/common/ServiceIsDown";
import TenantStats from "../components/TenantStats";
import TenantDBStats from "../components/TenantDBStats";
import ProgressBar from "../components/common/ProgressBar";
import CustomerPageScaffold from "../components/common/CustomerPageScaffold";
import MessageListPreview from "../components/MessageListPreview";
import HelpCenter from "../pages/HelpCenter";
import BillingInfo from "../components/billing/BillingInfo";
import InsightsSearchBox from "../components/reports/InsightsSearchBox";
import ReportsByWorkloadBox from "../components/reports/ReportsByWorkloadBox";

// Audit Vault Common
import { getCompanyByCompanyId, getCompanyByAzureTenantId } from "../utilities/common-company";

const theSectionTitle = "Welcome to Audit Vault for M365";
// Add playful random description to the Home Page.  
const messages = [
  "We love to manage your organization's vital M365 audit information!  Below you will find functions to help you access valuable insights from your audit data.",
  "Good day!  Thanks for choosing ECM Insights to manage your M365 audit management requirements.  Below you will find functions to help you access valuable insights from your audit data.",
  "Knowing is half the battle - Audit Vault for M365 preserves your valuable audit information.  Below you will find functions to help you access valuable insights from your audit data.",
  "Thanks for choosing Audit Vault for M365 to simply and powerfully preserve all of your M365 audit records. Below you will find functions to help you access valuable insights from your audit data.",
  "Unlock Enhanced Compliance Confidence in Microsoft M365 with Audit Vault for M365.",
  "Audit Vault for M365 empowers organizations to thrive in highly regulated environments by preserving, shielding, and intelligently surfacing your audit history information where and when you need it.",
  "A simply powerful and cost effective solution to retain and surface your organization's SharePoint Audit records for as long as you require.",
  "Audit Vault for M365 ensures your use of Microsoft 365 is GxP and 21 CFR Part 11 compliant, preventing costly non-compliance issues. ",
];


function MyHome(props) {

  const { user, serviceIsDownError, setServiceIsDownError, openCompanyNag, setOpenCompanyNag } = props;

  const { accounts } = useMsal();

  // Component Constants
  const [company, setCompany] = useState("");
  const [subscription, setSubscription] = useState("");
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  // Section titles.
  const [isAdminPage] = useState(false);
  const theBreadcrumbPath = [
    { name: 'Home', link: '/' }
  ];


  useEffect(() => {
    async function fetchData() {
      try {
        if (user) {
          if (user.isSystemAdmin && user.companyId == 0) {
            // Just a Audit Vault System Admin with no other company access
            navigate(`/Admin/AdminHome`)
          }
          else if (user.companyId == 0) {
            // try getting the company by azure tenant Id
            var checkResponse = await getCompanyByAzureTenantId(accounts[0].tenantId);
            if (checkResponse) {
              setCompany(checkResponse);
            }
          }
          else {
            // Load the company.
            const response = await getCompanyByCompanyId(user.companyId, setServiceIsDownError);
            if (response) {
              setCompany(response);
            }
          }
        }
      }
      catch (e) {
        console.log(e)
      }
      finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [user]);


  // Component UI
  if (serviceIsDownError) {
    return (
      <>
        <ServiceIsDown></ServiceIsDown>
      </>);
  }

  // Component UI
  if (loading) {
    return (<ProgressBar message="Loading ..." loading={loading} />);
  }

  if (!loading && !company) {
    // Occurs when logging in via Sign In button
    return <Navigate to="/MyCompanySetupCreate" replace />;
  }

  if (!loading && company && company.setupStageComplete == 0) {
    return <Navigate to="/MyCompanySetupUpdate" replace />;
  }

  if (!loading && company && company.setupStageComplete > 0)
    return (
      <>
        <CustomerPageScaffold breadcrumbPaths={theBreadcrumbPath} sectionTitle={theSectionTitle} sectionDescription="" isAdminPage={isAdminPage} company={company} openCompanyNag={openCompanyNag} setOpenCompanyNag={setOpenCompanyNag} />
        <Grid container spacing={2}>
          {(user.isCompanyAdmin || user.isTenantAdmin || user.isTenantReportReader || user.isTenantViewer) && user.tenantId > 0 && company.tenantList.map((tenant) => (
            <Grid
              key={`tenant-summary-${tenant.id}`}
              item
              xs={12}      // full width on phones
              md={6}       // 2 columns on md+
              sx={{ p: 2 }}
            >
              <Paper elevation={3} sx={{ p: 2, border: '1px solid lightgray', height: '100%' }}>
                <Typography component="div" variant="h6" sx={{ fontSize: 16, pb: 1 }}>
                  Tenant Summary
                </Typography>
                <TenantStats tenant={tenant} user={user} reportPageId={10} />
              </Paper>
            </Grid>
          ))}

          {(user.isCompanyAdmin || user.isTenantAdmin || user.isTenantReportReader || user.isTenantViewer) && user.tenantId > 0 && company.tenantList.map((tenant) => (
            <Grid
              key={`tenant-db-${tenant.id}`}
              item
              xs={12}
              md={6}
              sx={{ p: 2 }}
            >
              <Paper elevation={3} sx={{ p: 2, border: '1px solid lightgray', height: '100%' }}>
                <Typography component="div" variant="h6" sx={{ fontSize: 16, pb: 1 }}>
                  Tenant Database Storage Summary
                </Typography>
                <TenantDBStats
                  user={user}
                  tenant={tenant}
                  viewFullSize={true}
                  showLink={true}
                  showTenantUserCount={false}
                />
              </Paper>
            </Grid>
          ))}

          <Grid item xs={12} md={6} sx={{ p: 2 }}>
            <Paper elevation={3} sx={{ p: 2, border: '1px solid lightgray', height: '100%' }}>
              <Typography component="div" variant="h6" sx={{ fontSize: 16, pb: 1 }}>
                Message Center
              </Typography>
              <MessageListPreview theCompanyId={company.id} userMicrosoftId={user.microsoftUserId} />
              <br />
            </Paper>
          </Grid>

          <Grid item xs={12} md={6} sx={{ p: 2 }}>
            <Paper elevation={3} sx={{ p: 2, border: '1px solid lightgray', height: '100%' }}>
              <HelpCenter previewOnly={true} />
            </Paper>
          </Grid>

          {(user.isCompanyAdmin || user.isTenantAdmin || user.isTenantReportReader) && user.tenantId > 0 && (
            <>
              <InsightsSearchBox colSpanVal={6} workloadName={""} />
              <ReportsByWorkloadBox colSpanVal={6} />
            </>
          )}

          {(user.isCompanyAdmin) && user.tenantId > 0 && company.stripeCustomerId && subscription && (
            <Grid item xs={12} md={6} sx={{ p: 2 }}>
              <Paper
                key="billing"
                elevation={3}
                sx={{ p: 2, border: '1px solid lightgray', height: '100%' }}
              >
                <Typography component="div" variant="h6" sx={{ fontSize: 16, pb: 1 }}>
                  Billing and Payments
                </Typography>
                <BillingInfo subscription={subscription} companyId={company.id} />
                <br />
              </Paper>
            </Grid>
          )}
        </Grid>
      </>
    );
}
export default MyHome;
